import type { DocumentListFilters } from '@/lib/api/query-keys'
import { langCode, optional, withDefault, z } from '@/lib/utils/search-params'

/**
 * The Documents screen state is the URL (03-frontend §4.1). Every key falls back instead of
 * failing, so a stale bookmark still opens the list.
 */
export const DOCUMENT_TABS = ['all', 'in_progress', 'in_review', 'done'] as const
export const DOCUMENT_SORTS = ['name', 'pages', 'created_at', 'completed_at', 'last_activity_at'] as const

export const documentsSearchSchema = z.object({
  tab: withDefault(z.enum(DOCUMENT_TABS), 'all'),
  lang: optional(langCode),
  q: optional(z.string().check(z.trim(), z.minLength(1), z.maxLength(200))),
  sort: withDefault(z.enum(DOCUMENT_SORTS), 'created_at'),
  dir: withDefault(z.enum(['asc', 'desc']), 'desc'),
})

export type DocumentsSearch = z.infer<typeof documentsSearchSchema>
export type DocumentsTab = DocumentsSearch['tab']
export type DocumentsSort = DocumentsSearch['sort']

export const DEFAULT_DOCUMENTS_SEARCH: DocumentsSearch = { tab: 'all', sort: 'created_at', dir: 'desc' }

/** URL state -> the API's list parameters (sort is `-field` for descending). */
export function toListFilters(s: DocumentsSearch): DocumentListFilters {
  return {
    statusGroup: s.tab,
    sort: s.dir === 'desc' ? `-${s.sort}` : s.sort,
    ...(s.lang ? { targetLang: [s.lang] } : {}),
    ...(s.q ? { q: s.q } : {}),
  }
}

export function hasActiveFilters(s: DocumentsSearch): boolean {
  return s.tab !== 'all' || Boolean(s.lang) || Boolean(s.q)
}
